import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';

const CONDITIONS = [
  { key: 'min_diamonds', label: 'Diamonds >=', type: 'number' },
  { key: 'min_live_days', label: 'Jours live >=', type: 'number' },
  { key: 'min_live_hours', label: 'Heures live >=', type: 'number' },
  { key: 'min_tier', label: 'Tier actuel >=', type: 'select', options: [1,2,3,4,5,6,7,8,9] },
  { key: 'tier_evolution', label: 'Évolution tier (tier > tier M-1)', type: 'boolean' },
  { key: 'tier_maintien', label: 'Maintien tier (tier = tier M-1 ET tier >= T3)', type: 'boolean' },
];

const CashbackManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);

  // Editor state
  const [name, setName] = useState('');
  const [period, setPeriod] = useState('');
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  // FIX 2: View mode toggle
  const [viewMode, setViewMode] = useState('edit'); // 'edit' | 'preview'

  // Creator search state
  const [creatorSuggestions, setCreatorSuggestions] = useState({ ruleIndex: null, results: [] });

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session?.user?.email);
    });
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    setLoading(true);
    const { data } = await supabase?.from('cashback_programs')?.select('*, cashback_rules(*)')?.order('created_at', { ascending: false });
    setPrograms(data || []);
    setLoading(false);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // FIX 1: Load rules when clicking a program card
  const handleSelectProgram = (prog) => {
    setSelectedProgram(prog);
    setName(prog?.name || '');
    setPeriod(prog?.period || '');
    setRules(
      [...(prog?.cashback_rules || [])]?.sort((a, b) => a?.tier_order - b?.tier_order)?.map(r => ({
          ...r,
          conditions: r?.conditions || {},
          target_creator_id: r?.target_creator_id || null,
          creatorSearch: '',
        }))
    );
    setCreatorSuggestions({ ruleIndex: null, results: [] });
    setViewMode('edit');
  };

  const handleNewProgram = () => {
    const blank = { id: null, name: '', period: '', is_active: false, cashback_rules: [] };
    setSelectedProgram(blank);
    setName('');
    setPeriod('');
    setRules([]);
    setCreatorSuggestions({ ruleIndex: null, results: [] });
    setViewMode('edit');
  };

  const cancelEdit = () => {
    setSelectedProgram(null);
    setName('');
    setPeriod('');
    setRules([]);
    setCreatorSuggestions({ ruleIndex: null, results: [] });
    setViewMode('edit');
  };

  const handleToggle = async (prog, e) => {
    e?.stopPropagation();
    if (!prog?.is_active) {
      await supabase?.from('cashback_programs')?.update({ is_active: false })?.neq('id', prog?.id);
      await supabase?.from('cashback_programs')?.update({ is_active: true })?.eq('id', prog?.id);
    } else {
      await supabase?.from('cashback_programs')?.update({ is_active: false })?.eq('id', prog?.id);
    }
    fetchPrograms();
    if (selectedProgram?.id === prog?.id) {
      setSelectedProgram(prev => prev ? { ...prev, is_active: !prog?.is_active } : prev);
    }
  };

  // Rules state management
  const updateRule = (index, field, value) => {
    setRules(prev => prev?.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const updateCondition = (index, condKey, enabled, value) => {
    setRules(prev => prev?.map((r, i) => {
      if (i !== index) return r;
      const newConds = { ...r?.conditions };
      if (enabled) {
        newConds[condKey] = value;
      } else {
        delete newConds?.[condKey];
      }
      return { ...r, conditions: newConds };
    }));
  };

  const removeRule = (index) => {
    setRules(prev =>
      prev?.filter((_, i) => i !== index)?.map((r, i) => ({ ...r, tier_order: i + 1 }))
    );
  };

  const addRule = () => {
    setRules(prev => [...prev, {
      id: null,
      tier_order: prev?.length + 1,
      label: '',
      conditions: {},
      bonus_pct: 0,
      target_creator_id: null,
      creatorSearch: '',
      isNew: true,
    }]);
  };

  const searchCreator = async (query, ruleIndex) => {
    updateRule(ruleIndex, 'creatorSearch', query);
    if (query?.length < 2) {
      setCreatorSuggestions({ ruleIndex: null, results: [] });
      return;
    }
    const { data } = await supabase?.from('creators')?.select('tiktok_id, username, display_name, server')?.or(`username.ilike.%${query}%,tiktok_id.ilike.%${query}%`)?.limit(5);
    setCreatorSuggestions({ ruleIndex, results: data || [] });
  };

  const selectCreator = (ruleIndex, creator) => {
    updateRule(ruleIndex, 'target_creator_id', creator?.tiktok_id);
    updateRule(ruleIndex, 'creatorSearch', '');
    setCreatorSuggestions({ ruleIndex: null, results: [] });
  };

  const clearCreator = (ruleIndex) => {
    updateRule(ruleIndex, 'target_creator_id', null);
    updateRule(ruleIndex, 'creatorSearch', '');
  };

  const saveProgram = async () => {
    if (!name?.trim()) { showToast(t('admin.cashback.programNameRequired', 'Nom du programme requis')); return; }
    setSaving(true);
    try {
      const { data: prog } = await supabase?.from('cashback_programs')?.upsert({
        id: selectedProgram?.id || undefined,
        name,
        period,
        is_active: selectedProgram?.is_active || false,
        updated_at: new Date()?.toISOString(),
      })?.select()?.single();

      if (prog?.id) {
        await supabase?.from('cashback_rules')?.delete()?.eq('program_id', prog?.id);
      }

      if (rules?.length > 0 && prog?.id) {
        await supabase?.from('cashback_rules')?.insert(
          rules?.map((r, i) => ({
            program_id: prog?.id,
            tier_order: i + 1,
            label: r?.label || null,
            conditions: r?.target_creator_id ? {} : r?.conditions,
            bonus_pct: r?.bonus_pct,
            target_creator_id: r?.target_creator_id || null,
          }))
        );
      }

      await fetchPrograms();
      showToast(t('admin.cashback.programSaved', 'Programme sauvegardé ✓'));
      if (prog?.id) {
        setSelectedProgram(prev => prev ? { ...prev, id: prog?.id } : prev);
      }
    } catch (err) {
      showToast(t('admin.cashback.saveError', 'Erreur lors de la sauvegarde'));
    }
    setSaving(false);
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: 'var(--color-background)', color: '#e0e0e0' }}>
      <div className="hidden md:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(p => !p)}
          userEmail={userEmail}
        />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.cashback.title', 'Programme Cashback')} />
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0"
          style={{ borderColor: '#1f1f1f', backgroundColor: '#0d0d0d' }}
        >
          <div>
            <h1 className="text-white font-bold text-xl" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.cashback.title', 'Programme Cashback')}
            </h1>
            <p className="text-xs mt-0.5" style={{ color: '#555' }}>
              {t('admin.cashback.subtitle', 'Gestion des programmes de cashback')}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT PANEL — 35% desktop, full width mobile (hidden when program selected) */}
          <div
            className={`flex flex-col border-r overflow-hidden w-full md:w-[35%] md:min-w-[280px] ${selectedProgram ? 'hidden md:flex' : 'flex'}`}
            style={{ borderColor: '#1f1f1f', backgroundColor: '#0d0d0d' }}
          >
            {/* New program button */}
            <div className="px-4 pt-4 pb-3 flex-shrink-0">
              <button
                onClick={handleNewProgram}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all"
                style={{
                  backgroundColor: 'rgba(229,32,42,0.1)',
                  border: '1px solid rgba(229,32,42,0.3)',
                  color: '#FF4444',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(229,32,42,0.18)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(229,32,42,0.1)'}
              >
                <span style={{ fontSize: '16px' }}>+</span> {t('admin.cashback.newProgram', 'Nouveau programme')}
              </button>
            </div>

            {/* Programs list */}
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
              {loading ? (
                <div className="text-center py-8" style={{ color: '#555' }}>{t('admin.cashback.loading', 'Chargement...')}</div>
              ) : programs?.length === 0 ? (
                <div className="text-center py-8 text-sm" style={{ color: '#555' }}>{t('admin.cashback.noProgram', 'Aucun programme')}</div>
              ) : (
                programs?.map(prog => {
                  const isSelected = selectedProgram?.id === prog?.id;
                  return (
                    <div
                      key={prog?.id}
                      onClick={() => handleSelectProgram(prog)}
                      className="rounded-lg p-3 cursor-pointer transition-all"
                      style={{
                        backgroundColor: isSelected ? '#1a0a0a' : '#111',
                        border: isSelected ? '1px solid #E5202A' : '1px solid #1f1f1f',
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-white truncate">{prog?.name || '—'}</div>
                          {prog?.period && (
                            <div
                              className="inline-block mt-1 text-xs px-2 py-0.5 rounded"
                              style={{ backgroundColor: '#1f1f1f', color: '#888' }}
                            >
                              {prog?.period}
                            </div>
                          )}
                          <div className="text-xs mt-1.5" style={{ color: '#555' }}>
                            {prog?.cashback_rules?.length || 0} {(prog?.cashback_rules?.length || 0) !== 1 ? t('admin.cashback.rulesCountPlural', 'règles') : t('admin.cashback.rulesCount', 'règle')}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <span
                            className="text-xs font-bold px-2 py-0.5 rounded"
                            style={prog?.is_active
                              ? { backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e' }
                              : { backgroundColor: 'rgba(128,128,128,0.1)', color: '#555' }
                            }
                          >
                            {prog?.is_active ? t('admin.cashback.active', 'ACTIF') : t('admin.cashback.inactive', 'INACTIF')}
                          </span>
                          <button
                            onClick={e => handleToggle(prog, e)}
                            className="relative flex-shrink-0 transition-all"
                            style={{ width: '36px', height: '20px' }}
                            title={prog?.is_active ? 'Désactiver' : 'Activer'}
                          >
                            <div
                              className="w-full h-full rounded-full transition-all"
                              style={{
                                backgroundColor: prog?.is_active ? '#22c55e' : '#2a2a2a',
                                border: prog?.is_active ? '1px solid #22c55e' : '1px solid #333',
                              }}
                            />
                            <div
                              className="absolute top-0.5 rounded-full transition-all"
                              style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: '#fff',
                                left: prog?.is_active ? '18px' : '2px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                              }}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT PANEL — 65% desktop, full width mobile (shown when program selected) */}
          <div className={`flex-1 flex flex-col overflow-hidden ${!selectedProgram ? 'hidden md:flex' : 'flex'}`} style={{ backgroundColor: 'var(--color-background)' }}>
            {!selectedProgram ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center" style={{ color: '#333' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
                  <div className="text-sm">{t('admin.cashback.selectOrCreate', 'Sélectionnez un programme ou créez-en un nouveau')}</div>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-4 md:p-6">
                {/* Mobile back button */}
                <button onClick={cancelEdit} className="md:hidden flex items-center gap-2 mb-4 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  {t('admin.cashback.backToList', 'Retour aux programmes')}
                </button>
                {/* Header form */}
                <div
                  className="rounded-xl p-5 mb-6"
                  style={{ backgroundColor: '#0d0d0d', border: '1px solid #1f1f1f' }}
                >
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[180px]">
                      <label className="block text-xs mb-1.5" style={{ color: '#666' }}>{t('admin.cashback.programName', 'Nom du programme')}</label>
                      <input
                        placeholder={t('admin.cashback.programName', 'Nom du programme')}
                        value={name}
                        onChange={e => setName(e?.target?.value)}
                        className="w-full rounded-lg px-3 py-2 text-base text-white outline-none transition-all"
                        style={{ backgroundColor: '#111', border: '1px solid #2a2a2a', color: '#e0e0e0' }}
                        onFocus={e => e.target.style.borderColor = 'var(--color-primary)'}
                        onBlur={e => e.target.style.borderColor = '#2a2a2a'}
                      />
                    </div>
                    <div style={{ minWidth: '160px' }}>
                      <label className="block text-xs mb-1.5" style={{ color: '#666' }}>{t('admin.cashback.period', 'Période')}</label>
                      <input
                        placeholder={t('admin.cashback.periodPlaceholder', 'ex: 2026-03')}
                        value={period}
                        onChange={e => setPeriod(e?.target?.value)}
                        className="w-full rounded-lg px-3 py-2 text-base outline-none transition-all"
                        style={{ backgroundColor: '#111', border: '1px solid #2a2a2a', color: '#e0e0e0' }}
                        onFocus={e => e.target.style.borderColor = 'var(--color-primary)'}
                        onBlur={e => e.target.style.borderColor = '#2a2a2a'}
                      />
                    </div>
                    <div className="flex gap-2 items-end">
                      {/* FIX 2: Modifier / Aperçu toggle */}
                      <div
                        className="flex rounded-lg overflow-hidden"
                        style={{ border: '1px solid #2a2a2a' }}
                      >
                        <button
                          onClick={() => setViewMode('edit')}
                          className="px-3 py-2 text-xs font-semibold transition-all"
                          style={{
                            backgroundColor: viewMode === 'edit' ? '#E5202A' : 'transparent',
                            color: viewMode === 'edit' ? '#fff' : '#666',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          {t('admin.cashback.edit', 'Modifier')}
                        </button>
                        <button
                          onClick={() => setViewMode('preview')}
                          className="px-3 py-2 text-xs font-semibold transition-all"
                          style={{
                            backgroundColor: viewMode === 'preview' ? '#E5202A' : 'transparent',
                            color: viewMode === 'preview' ? '#fff' : '#666',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          {t('admin.cashback.preview', 'Aperçu')}
                        </button>
                      </div>
                      <button
                        onClick={saveProgram}
                        disabled={saving}
                        className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                        style={{
                          backgroundColor: saving ? '#2a2a2a' : '#E5202A',
                          color: saving ? '#555' : '#fff',
                          border: 'none',
                          cursor: saving ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {saving ? t('admin.cashback.saving', 'Sauvegarde...') : t('admin.cashback.save', 'Sauvegarder')}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid #2a2a2a',
                          color: '#888',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#555'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#2a2a2a'}
                      >
                        {t('admin.cashback.cancel', 'Annuler')}
                      </button>
                    </div>
                  </div>
                </div>

                {/* FIX 2: Preview mode */}
                {viewMode === 'preview' ? (
                  <PreviewPanel
                    name={name}
                    period={period}
                    is_active={selectedProgram?.is_active}
                    rules={rules}
                  />
                ) : (
                  <>
                    {/* Rules section header */}
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                        {t('admin.cashback.rules', 'Règles de cashback')}
                        <span className="ml-2 text-xs font-normal" style={{ color: '#555' }}>
                          ({rules?.length} {rules?.length !== 1 ? t('admin.cashback.rulesCountPlural', 'règles') : t('admin.cashback.rulesCount', 'règle')})
                        </span>
                      </h2>
                    </div>

                    {/* FIX 3: OR logic info banner */}
                    <div
                      className="rounded-lg px-4 py-3 mb-4 text-xs"
                      style={{
                        backgroundColor: 'rgba(59,130,246,0.08)',
                        border: '1px solid rgba(59,130,246,0.25)',
                        color: '#93c5fd',
                        lineHeight: '1.6',
                      }}
                    >
                      ℹ️ {t('admin.cashback.rulesInfo', 'Les règles sont évaluées dans l\'ordre (OU) — placez les règles les plus avantageuses en premier. La première règle satisfaite s\'applique au créateur. Les règles individuelles créateur sont toujours prioritaires.')}
                    </div>

                    {/* Rules list */}
                    <div className="space-y-3 mb-4">
                      {rules?.map((rule, i) => (
                        <RuleCard
                          key={i}
                          rule={rule}
                          index={i}
                          updateRule={updateRule}
                          updateCondition={updateCondition}
                          removeRule={removeRule}
                          searchCreator={searchCreator}
                          selectCreator={selectCreator}
                          clearCreator={clearCreator}
                          creatorSuggestions={creatorSuggestions}
                          setCreatorSuggestions={setCreatorSuggestions}
                        />
                      ))}
                    </div>

                    {/* Add rule button */}
                    <button
                      onClick={addRule}
                      className="w-full py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1.5px dashed #2a2a2a',
                        color: '#555',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.color = '#FF4444'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#555'; }}
                    >
                      <span style={{ fontSize: '18px' }}>+</span> {t('admin.cashback.addRule', 'Ajouter une règle')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-semibold z-50"
          style={{ backgroundColor: '#1a1a1a', border: '1px solid #333', color: '#e0e0e0', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}
        >
          {toast}
        </div>
      )}
      <AdminBottomNav />
    </div>
  );
};

// ─── Preview Panel Component ─────────────────────────────────────────────────

const PreviewPanel = ({ name, period, is_active, rules }) => {
  const individualRules = rules?.filter(r => r?.target_creator_id);
  const generalRules = rules?.filter(r => !r?.target_creator_id)?.sort((a, b) => a?.tier_order - b?.tier_order);

  return (
    <div>
      {/* Program header */}
      <div
        className="rounded-xl p-5 mb-5"
        style={{ backgroundColor: '#0d0d0d', border: '1px solid #1f1f1f' }}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-bold text-white">{name || '—'}</h2>
          {period && (
            <span
              className="text-xs px-2 py-0.5 rounded"
              style={{ backgroundColor: '#1f1f1f', color: '#888' }}
            >
              Période: {period}
            </span>
          )}
          <span
            className="text-xs font-bold px-2 py-0.5 rounded"
            style={is_active
              ? { backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e' }
              : { backgroundColor: 'rgba(128,128,128,0.1)', color: '#555' }
            }
          >
            {is_active ? 'ACTIF' : 'INACTIF'}
          </span>
        </div>
        <p className="text-xs mt-2" style={{ color: '#555', lineHeight: '1.6' }}>
          Les règles sont évaluées dans l'ordre — la première règle satisfaite s'applique (OU entre règles). Les règles individuelles créateur sont toujours prioritaires.
        </p>
      </div>
      {/* Individual creator rules */}
      {individualRules?.length > 0 && (
        <div className="mb-5">
          <div
            className="text-xs font-bold uppercase tracking-wider mb-3 px-1"
            style={{ color: '#a855f7' }}
          >
            Règles individuelles (priorité absolue)
          </div>
          <div className="space-y-2">
            {individualRules?.map((r, i) => (
              <div
                key={i}
                className="rounded-lg p-3 flex items-center justify-between gap-3"
                style={{
                  backgroundColor: '#111',
                  border: '1px solid #1f1f1f',
                  borderLeft: '3px solid #a855f7',
                }}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white">
                    {r?.label || 'Règle individuelle'}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: '#a855f7' }}>
                    Créateur: {r?.target_creator_id}
                  </div>
                </div>
                <div
                  className="text-sm font-bold flex-shrink-0"
                  style={{ color: '#a855f7' }}
                >
                  {r?.bonus_pct}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* General rules */}
      <div>
        <div
          className="text-xs font-bold uppercase tracking-wider mb-3 px-1"
          style={{ color: '#FF4444' }}
        >
          Règles générales — évaluées dans l'ordre ↓
        </div>
        <div className="space-y-2">
          {generalRules?.map((r, i) => (
            <div
              key={i}
              className="rounded-lg p-3 flex items-center gap-3"
              style={{
                backgroundColor: '#111',
                border: '1px solid #1f1f1f',
                borderLeft: '3px solid #E5202A',
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs" style={{ color: '#555' }}>
                    {i === 0 ? 'Si satisfaite →' : 'Sinon si satisfaite →'}
                  </span>
                  <span className="text-sm font-semibold text-white">
                    {r?.label || `Règle ${i + 1}`}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {r?.conditions?.min_diamonds !== undefined && (
                    <span
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ backgroundColor: '#1a1a1a', color: '#ccc', border: '1px solid #2a2a2a' }}
                    >
                      💎 &gt;= {r?.conditions?.min_diamonds?.toLocaleString()}
                    </span>
                  )}
                  {r?.conditions?.min_live_days !== undefined && (
                    <span
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ backgroundColor: '#1a1a1a', color: '#ccc', border: '1px solid #2a2a2a' }}
                    >
                      📅 &gt;= {r?.conditions?.min_live_days}j
                    </span>
                  )}
                  {r?.conditions?.min_live_hours !== undefined && (
                    <span
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ backgroundColor: '#1a1a1a', color: '#ccc', border: '1px solid #2a2a2a' }}
                    >
                      ⏱ &gt;= {r?.conditions?.min_live_hours}h
                    </span>
                  )}
                  {r?.conditions?.min_tier !== undefined && (
                    <span
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ backgroundColor: '#1a1a1a', color: '#ccc', border: '1px solid #2a2a2a' }}
                    >
                      🏆 Tier &gt;= T{r?.conditions?.min_tier}
                    </span>
                  )}
                  {r?.conditions?.tier_evolution && (
                    <span
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ backgroundColor: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)' }}
                    >
                      ↑ Évolution tier
                    </span>
                  )}
                  {r?.conditions?.tier_maintien && (
                    <span
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ backgroundColor: 'rgba(59,130,246,0.1)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)' }}
                    >
                      = Maintien tier (&gt;=T3)
                    </span>
                  )}
                  {Object.keys(r?.conditions || {})?.length === 0 && (
                    <span className="text-xs" style={{ color: '#555', fontStyle: 'italic' }}>
                      Aucune condition (s'applique à tous)
                    </span>
                  )}
                </div>
              </div>
              <div
                className="text-sm font-bold flex-shrink-0"
                style={{ color: '#FF4444' }}
              >
                {r?.bonus_pct}%
              </div>
            </div>
          ))}

          {/* Fallback row */}
          <div
            className="rounded-lg p-3 flex items-center justify-between gap-3"
            style={{
              backgroundColor: '#0d0d0d',
              border: '1px solid #1f1f1f',
              borderLeft: '3px solid #333',
            }}
          >
            <span className="text-xs" style={{ color: '#444' }}>
              Sinon → Aucun cashback (0%)
            </span>
            <span className="text-sm font-bold" style={{ color: '#444' }}>0%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Rule Card Component ─────────────────────────────────────────────────────

const RuleCard = ({
  rule, index,
  updateRule, updateCondition, removeRule,
  searchCreator, selectCreator, clearCreator,
  creatorSuggestions, setCreatorSuggestions,
}) => {
  const isIndividual = !!rule?.target_creator_id;
  const [individualToggle, setIndividualToggle] = useState(isIndividual || !!rule?.creatorSearch);

  const handleIndividualToggle = () => {
    const next = !individualToggle;
    setIndividualToggle(next);
    if (!next) {
      clearCreator(index);
    }
  };

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        backgroundColor: '#111',
        border: '1px solid #1f1f1f',
        borderLeft: '3px solid #E5202A',
      }}
    >
      {/* Rule header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: '#1f1f1f' }}
      >
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#FF4444' }}>
          Règle {index + 1}
        </span>
        <button
          onClick={() => removeRule(index)}
          className="text-xs px-2 py-1 rounded transition-all"
          style={{ color: '#555', backgroundColor: 'transparent', border: '1px solid #2a2a2a', cursor: 'pointer' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#FF4444'; e.currentTarget.style.borderColor = '#FF4444'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#555'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
        >
          × Supprimer
        </button>
      </div>
      <div className="p-4 space-y-4">
        {/* Label */}
        <div>
          <input
            placeholder="Label (ex: Bronze Évolution)"
            value={rule?.label || ''}
            onChange={e => updateRule(index, 'label', e?.target?.value)}
            className="w-full rounded-lg px-3 py-2 text-base outline-none transition-all"
            style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: '#e0e0e0' }}
            onFocus={e => e.target.style.borderColor = 'var(--color-primary)'}
            onBlur={e => e.target.style.borderColor = '#2a2a2a'}
          />
        </div>

        {/* Individual creator toggle */}
        <div>
          <label
            className="flex items-center gap-2 cursor-pointer select-none text-sm"
            style={{ color: individualToggle ? '#e0e0e0' : '#666' }}
          >
            <input
              type="checkbox"
              checked={individualToggle}
              onChange={handleIndividualToggle}
              style={{ accentColor: 'var(--color-primary)', width: '14px', height: '14px', cursor: 'pointer' }}
            />
            ☑ Règle individuelle créateur
          </label>

          {individualToggle && (
            <div className="mt-2 relative">
              {rule?.target_creator_id ? (
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}
                  >
                    {rule?.target_creator_id} ✓
                    <button
                      onClick={() => clearCreator(index)}
                      className="ml-1 hover:text-white transition-colors"
                      style={{ color: '#22c55e', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}
                    >
                      ×
                    </button>
                  </span>
                </div>
              ) : (
                <>
                  <input
                    placeholder="Rechercher username ou TikTok ID..."
                    value={rule?.creatorSearch || ''}
                    onChange={e => searchCreator(e?.target?.value, index)}
                    className="w-full rounded-lg px-3 py-2 text-base outline-none transition-all"
                    style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: '#e0e0e0' }}
                    onFocus={e => e.target.style.borderColor = 'var(--color-primary)'}
                    onBlur={e => e.target.style.borderColor = '#2a2a2a'}
                  />
                  {creatorSuggestions?.ruleIndex === index && creatorSuggestions?.results?.length > 0 && (
                    <div
                      className="absolute z-20 w-full mt-1 rounded-lg overflow-hidden"
                      style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}
                    >
                      {creatorSuggestions?.results?.map(c => (
                        <button
                          key={c?.tiktok_id}
                          onClick={() => selectCreator(index, c)}
                          className="w-full text-left px-3 py-2 text-xs transition-all"
                          style={{ color: '#ccc', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <span className="font-semibold text-white">{c?.username}</span>
                          <span style={{ color: '#666' }}> — {c?.server} — </span>
                          <span style={{ color: '#888' }}>{c?.tiktok_id}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Conditions (hidden if individual creator set) */}
        {!rule?.target_creator_id && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#444' }}>
              Conditions
            </div>
            <div className="space-y-1">
              {CONDITIONS?.map(cond => {
                const isChecked = rule?.conditions?.[cond?.key] !== undefined;
                return (
                  <div
                    key={cond?.key}
                    className="flex items-center gap-2"
                    style={{ height: '36px' }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={e => updateCondition(
                        index, cond?.key, e?.target?.checked,
                        cond?.type === 'boolean' ? true : 0
                      )}
                      style={{ accentColor: 'var(--color-primary)', width: '13px', height: '13px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <label className="text-xs flex-shrink-0" style={{ color: isChecked ? '#ccc' : '#555', minWidth: '180px' }}>
                      {cond?.label}
                    </label>
                    {isChecked && cond?.type === 'number' && (
                      <input
                        type="number"
                        value={rule?.conditions?.[cond?.key]}
                        onChange={e => updateCondition(index, cond?.key, true, parseFloat(e?.target?.value))}
                        className="rounded px-2 py-1 text-base outline-none"
                        style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: '#e0e0e0', width: '80px' }}
                      />
                    )}
                    {isChecked && cond?.type === 'select' && (
                      <select
                        value={rule?.conditions?.[cond?.key]}
                        onChange={e => updateCondition(index, cond?.key, true, parseInt(e?.target?.value))}
                        className="rounded px-2 py-1 text-base outline-none"
                        style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: '#e0e0e0', width: '70px' }}
                      >
                        {[1,2,3,4,5,6,7,8,9]?.map(n => (
                          <option key={n} value={n}>T{n}</option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bonus */}
        <div
          className="flex items-center gap-3 pt-3 border-t"
          style={{ borderColor: '#1f1f1f' }}
        >
          <label className="text-xs font-semibold" style={{ color: '#888' }}>Taux cashback :</label>
          <input
            type="number"
            step="0.1"
            value={rule?.bonus_pct || 0}
            onChange={e => updateRule(index, 'bonus_pct', parseFloat(e?.target?.value))}
            className="rounded px-2 py-1 text-base outline-none"
            style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: '#e0e0e0', width: '70px' }}
          />
          <span className="text-sm font-bold" style={{ color: '#FF4444' }}>%</span>
          <span className="text-xs" style={{ color: '#444' }}>des diamonds du créateur</span>
        </div>
      </div>
    </div>
  );
};

export default CashbackManagement;
